import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Empty,
  Flex,
  Popconfirm,
  Switch,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { Boxes, Plug, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceAgent } from 'src/api/facade/types'
import { applicationsApplicationsListOptions } from 'src/api/main'
import { ContentSpinner, MonoValue, QueryErrorAlert } from 'src/components'
import type { SquadMcpCatalogEntry } from 'src/services/mcpServers'
import { useScopeCatalog } from 'src/services/scopeCatalog'
import { useShowApiErrors } from 'src/utils'

import type { CatalogConnection } from './connectionModel'
import { ConnectionScopeEditor } from './ConnectionScopeEditor'
import {
  useAgentConnections,
  useConnectMcp,
  useDetachAgentMcp,
  useToggleAgentMcp
} from './useAgentConnections'

const { Text } = Typography

type Props = {
  workspaceId: number
  agent: WorkspaceAgent
}

/**
 * MCP tab — connect remote MCP servers to the agent, toggle/detach them, and
 * grant each connection its delegated scopes inline (see
 * {@link ConnectionScopeEditor}) so connecting a tool and setting its
 * permissions happen in one place. Distinct from the Connections tab, which
 * manages the agent's built-in function tools.
 */
export const AgentMcpTab = ({ workspaceId, agent }: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()
  const readOnly = agent.canEdit === false

  const { connections, isLoading, isError } = useAgentConnections(
    workspaceId,
    agent.id
  )
  const { data: scopeCatalog = [] } = useScopeCatalog()
  const connectMut = useConnectMcp(workspaceId, agent.id)
  const toggleMut = useToggleAgentMcp(workspaceId, agent.id)
  const detachMut = useDetachAgentMcp(workspaceId, agent.id)

  // The link just connected — its permission editor opens so the user can grant
  // scopes immediately after connecting.
  const [justConnectedLink, setJustConnectedLink] = useState<number | null>(
    null
  )

  // The org's apps — used to resolve the single cross-app (CRM/HRM) instance
  // to connect.
  const { data: appsData } = useQuery({
    ...applicationsApplicationsListOptions({
      query: { organization: workspaceId, per_page: 100 }
    }),
    enabled: workspaceId > 0
  })

  const instancesForType = (type: string) =>
    (appsData?.results ?? [])
      .filter((a) => a.instance_type?.type === type)
      .map((a) => ({ token: a.token, name: a.name }))

  const connecting = (slug: string) =>
    connectMut.isPending && connectMut.variables?.slug === slug

  const doConnect = (input: {
    slug: string
    app_token?: string
    name?: string
  }) =>
    connectMut.mutate(input, {
      onSuccess: (link) => {
        message.success(t('workspace.agents.mcp.connected'))
        // Open the new connection's permission editor so scopes can be granted
        // straight away.
        setJustConnectedLink(link.id)
      },
      onError: (e) => showApiErrors(e)
    })

  const onConnect = (entry: SquadMcpCatalogEntry) => {
    if (!entry.target_app_type) {
      doConnect({ slug: entry.slug })
      return
    }
    // CRM/HRM are single-instance per org: connect to that one instance.
    const instances = instancesForType(entry.target_app_type)
    if (instances.length === 1) {
      doConnect({
        slug: entry.slug,
        app_token: instances[0].token,
        name: instances[0].name
      })
      return
    }
    // No instance installed for this org — nothing to connect.
    message.error(
      t('workspace.agents.mcp.noInstance', {
        app: entry.target_app_type.toUpperCase()
      })
    )
  }

  const toggle = (linkId: number, isEnabled: boolean) =>
    toggleMut.mutate(
      { linkId, isEnabled },
      { onError: (e) => showApiErrors(e) }
    )

  const detach = (linkId: number) =>
    detachMut.mutate(linkId, {
      onSuccess: () => message.success(t('workspace.agents.mcp.detached')),
      onError: (e) => showApiErrors(e)
    })

  const renderConnectAction = (conn: CatalogConnection) => (
    <Button
      size="small"
      type="primary"
      icon={<Plus size={14} />}
      disabled={readOnly}
      loading={connecting(conn.entry.slug)}
      onClick={() => onConnect(conn.entry)}
    >
      {t('workspace.agents.mcp.connect')}
    </Button>
  )

  const renderRow = (conn: CatalogConnection) => (
    <div key={conn.entry.slug}>
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        style={{
          padding: '10px 12px',
          background: 'var(--ant-color-fill-tertiary)',
          borderBottom: '1px solid var(--ant-color-border-secondary)'
        }}
      >
        <Flex align="center" gap={10} style={{ minWidth: 0 }}>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              background: 'var(--ant-color-bg-container)',
              border: '1px solid var(--ant-color-border-secondary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
              color: 'var(--ant-color-text-secondary)'
            }}
          >
            {conn.entry.icon_url ? (
              <img
                src={conn.entry.icon_url}
                alt=""
                width={16}
                height={16}
                style={{ borderRadius: 3 }}
              />
            ) : conn.isCrossApp ? (
              <Boxes size={15} />
            ) : (
              <Plug size={15} />
            )}
          </span>
          <Flex vertical style={{ minWidth: 0 }}>
            <Flex align="center" gap={6} wrap="wrap">
              <Text strong style={{ fontSize: 13 }}>
                {conn.entry.name}
              </Text>
              <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>
                {conn.app.toUpperCase()}
              </Tag>
              {conn.attached ? (
                <Tag
                  color="success"
                  style={{ marginInlineEnd: 0, fontSize: 11 }}
                >
                  {t('workspace.agents.mcp.connectedTag')}
                </Tag>
              ) : null}
            </Flex>
            {conn.entry.description ? (
              <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                {conn.entry.description}
              </Text>
            ) : null}
          </Flex>
        </Flex>
        {!conn.attached && renderConnectAction(conn)}
      </Flex>

      {conn.links.map(({ link }) => (
        <div key={link.id}>
          <Flex
            align="center"
            justify="space-between"
            gap={12}
            style={{ padding: '8px 12px 8px 48px' }}
          >
            <Flex vertical style={{ minWidth: 0 }} gap={2}>
              <Text style={{ fontSize: 12.5 }} ellipsis>
                {link.server_name}
              </Text>
              <MonoValue copyable={false} ellipsis fontSize={11}>
                {link.server_url}
              </MonoValue>
            </Flex>
            <Flex align="center" gap={10} style={{ flex: '0 0 auto' }}>
              <Tooltip
                title={
                  link.is_enabled
                    ? t('workspace.agents.mcp.enabledTip')
                    : t('workspace.agents.mcp.disabledTip')
                }
              >
                <Switch
                  size="small"
                  checked={link.is_enabled}
                  disabled={readOnly || toggleMut.isPending}
                  onChange={(v) => toggle(link.id, v)}
                />
              </Tooltip>
              <Popconfirm
                title={t('workspace.agents.mcp.detachConfirm')}
                okText={t('workspace.agents.mcp.detach')}
                okButtonProps={{ danger: true }}
                cancelText={t('workspace.agents.form.cancel')}
                onConfirm={() => detach(link.id)}
              >
                <Button
                  size="small"
                  danger
                  type="text"
                  disabled={readOnly}
                  icon={<Trash2 size={14} />}
                  aria-label={t('workspace.agents.mcp.detach')}
                />
              </Popconfirm>
            </Flex>
          </Flex>

          <ConnectionScopeEditor
            workspaceId={workspaceId}
            agent={agent}
            link={link}
            appCatalog={scopeCatalog.filter((r) => r.app === conn.app)}
            readOnly={readOnly}
            defaultOpen={link.id === justConnectedLink}
          />
        </div>
      ))}
    </div>
  )

  return (
    <Flex vertical gap={14}>
      <Alert
        type="info"
        showIcon
        message={t('workspace.agents.mcp.introTitle')}
        description={t('workspace.agents.mcp.introBody')}
      />

      <Card
        title={t('workspace.agents.mcp.title')}
        styles={{ body: { padding: 0 } }}
      >
        {isError ? (
          <div style={{ padding: 14 }}>
            <QueryErrorAlert />
          </div>
        ) : isLoading ? (
          <ContentSpinner minHeight={160} />
        ) : connections.length === 0 ? (
          <div style={{ padding: 24 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('workspace.agents.mcp.empty')}
                </Text>
              }
            />
          </div>
        ) : (
          <div>{connections.map(renderRow)}</div>
        )}
      </Card>
    </Flex>
  )
}
